import { createRef, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import type { MouseEventHandler, RefObject } from 'react';

import styles from './CheckboxTree.module.css';

import { Button, Checkbox, FormField, Icon, Label } from 'components';
import { useResponsive } from 'components/hooks/useResponsive';

// TODO: Currently the checkbox tree shows odd behavior when a disabled checkbox is checked.
// When deselecting the parent checkbox, the disabled checkbox is deselected, and will never
// be checked again. We'll leave this for now, until we run into a real world
// example where this is necessary.

export type CheckboxLeaf = {
  itemName: string;
  itemValue: string;
  disabled?: boolean;
};

export type CheckboxBranch = {
  itemName: string;
  children: CheckboxLeaf[];
};

export interface CheckboxTreeProps {
  /** The title of the component. */
  title?: string;
  /** Array of checkbox groups with a label and array of children (itemName, itemValue and disabled boolean). */
  checkboxTree: CheckboxBranch[];
  /** Array of selected values. */
  selectedValues?: string[];
  /** `true` if the font-weight needs to be unset; otherwise, `false`. The default is `false`. */
  noLabelFrontWeight?: boolean;
  /** Returns array of selected values. */
  onChange?: (value: string[]) => void;
}

/** Represents a component to render a tree of checkboxes. */
export const CheckboxTree = ({
  title,
  checkboxTree,
  selectedValues,
  noLabelFrontWeight = false,
  onChange,
}: CheckboxTreeProps) => {
  const checkboxRefs = useRef<Record<string, RefObject<HTMLInputElement | null>>>({});
  const [values, setValues] = useState<string[]>([]);
  const { lessThan } = useResponsive();

  const valuesAreEqual = (values1: string[], values2: string[]): boolean => {
    if (values1.length !== values2.length) return false;

    const countElements = (list: string[]) =>
      list.reduce((acc: Record<string, number>, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});

    const numberOfValues1 = countElements(values1);
    const numberOfValues2 = countElements(values2);

    return !Object.keys(numberOfValues1).some((key) => numberOfValues1[key] !== numberOfValues2[key]);
  };

  const createRefForKey = (key: string) => {
    if (!checkboxRefs.current[key]) {
      checkboxRefs.current[key] = createRef();
    }
    return checkboxRefs.current[key];
  };

  const setValue = () => {
    const newValues: string[] = [];
    Object.entries(checkboxRefs.current).forEach(([key]) => {
      const chkBox = checkboxRefs.current[key].current;
      if (chkBox && chkBox.name.indexOf('Group') === -1 && chkBox.checked) {
        newValues.push(chkBox.value);
      }
    });
    setValues(newValues);
    if (onChange) onChange(newValues);
  };

  const setCheckboxesState = (checkGroupName: string) => {
    if (checkGroupName === '') return;

    const groupStatus: boolean[] = [];

    Object.entries(checkboxRefs.current).forEach(([key]) => {
      const chkBox = checkboxRefs.current[key].current;
      if (!chkBox) return;
      if (chkBox?.getAttribute('data-groupname') === checkGroupName) {
        groupStatus.push(chkBox.checked);
      }
    });

    const every = groupStatus.every(Boolean);
    const some = groupStatus.some(Boolean);

    if (checkboxRefs.current[checkGroupName].current) {
      checkboxRefs.current[checkGroupName].current.checked = every;
      checkboxRefs.current[checkGroupName].current.indeterminate = !every && every !== some;
      checkboxRefs.current[checkGroupName].current.checked = every && every === some;
    }
  };

  const setExpandedAttributes = (trigger: HTMLButtonElement, container: Element | null) => {
    const children = container?.querySelector('[data-ul]');

    (trigger.childNodes[0] as Element).classList.toggle(styles['checkbox-tree__branch--icon--expanded']);
    container
      ?.closest('[data-li]')
      ?.setAttribute(
        'aria-expanded',
        container?.closest('[data-li]')?.getAttribute('aria-expanded') === 'false' ? 'true' : 'false'
      );

    if (children) {
      children.classList.toggle(styles['checkbox-tree__leaves--expanded']);
    }
  };

  const handleChange: MouseEventHandler<HTMLDivElement> = (event) => {
    const check = event.target as HTMLInputElement;

    if (check.type !== 'checkbox' && check.type !== 'button') return;

    if (
      lessThan.md &&
      check.name.indexOf('Group') !== -1 &&
      check.parentNode &&
      (check.parentNode.parentNode as HTMLElement).getAttribute('aria-expanded') === 'false'
    ) {
      event.preventDefault();
      setExpandedAttributes(check.parentNode as HTMLButtonElement, check.parentNode.parentNode as HTMLElement);
      return;
    }

    if (check.name.indexOf('Group') !== -1) {
      const childPrefix = check.name;
      Object.entries(checkboxRefs.current).forEach(([key]) => {
        if (key.indexOf(`${childPrefix}_`) !== -1) {
          const chkBox = checkboxRefs.current[key].current;
          const parentCheckbox = checkboxRefs.current[childPrefix].current;
          if (!chkBox || !parentCheckbox) return;
          chkBox.checked = parentCheckbox.checked ? true : false;
        }
      });
      setCheckboxesState(childPrefix);
      setValue();
      return;
    }
    setCheckboxesState(check.getAttribute('data-groupname') || '');
    setValue();
  };

  const handleExpanded: MouseEventHandler<HTMLButtonElement> = (event) => {
    const target = event.target as HTMLButtonElement;
    const parent = target.closest('[data-li]');
    setExpandedAttributes(target, parent);
  };

  useEffect(() => {
    if (selectedValues && !valuesAreEqual(selectedValues, values)) {
      Object.entries(checkboxRefs.current).forEach(([key]) => {
        const chkBox = checkboxRefs.current[key].current;
        if (!chkBox) return;
        if (selectedValues?.includes(chkBox.value)) {
          chkBox.checked = true;
        } else {
          chkBox.checked = false;
        }
        if (chkBox.name.indexOf('Group') !== -1) {
          setValues(selectedValues || []);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues]);

  useEffect(() => {
    Object.entries(checkboxRefs.current).forEach(([key]) => {
      const chkBox = checkboxRefs.current[key].current;
      if (!chkBox) return;
      if (chkBox.name.indexOf('Group') !== -1) {
        setCheckboxesState(chkBox.name);
      }
    });
  }, [values]);

  return (
    <div>
      {title && <span className={styles['checkbox-tree__header']}>{title}</span>}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div onClick={handleChange} data-ul role="tree" tabIndex={0}>
        {checkboxTree.map(({ itemName, children }, index) => (
          <div
            key={itemName}
            className={styles['checkbox-tree__branch']}
            data-li
            aria-expanded={lessThan.md ? 'false' : 'true'}
          >
            <FormField inline className={styles['checkbox-tree__branch--formfield']}>
              <Button variant="blank" onClick={handleExpanded}>
                <Icon
                  name="CheveronRight"
                  size={16}
                  className={clsx(styles['checkbox-tree__branch--icon'], {
                    [styles['checkbox-tree__branch--icon--expanded']]: !lessThan.md,
                  })}
                />
              </Button>
              <Checkbox
                id={itemName}
                name={`Group${index}`}
                disabled={children.every((checkbox) => checkbox.disabled)}
                ref={createRefForKey(`Group${index}`)}
              />
              <Label htmlFor={itemName} noFontWeight={noLabelFrontWeight}>
                {itemName}
              </Label>
            </FormField>
            <div
              className={clsx(styles['checkbox-tree__leaves'], {
                [styles['checkbox-tree__leaves--expanded']]: !lessThan.md,
              })}
              data-ul
            >
              {children.map(({ itemName, itemValue, disabled }) => (
                <div key={itemName} className={styles['checkbox-tree__leaf']} data-li>
                  <FormField inline className={styles['checkbox-tree__branch--formfield']}>
                    <Checkbox
                      id={itemName}
                      name={itemName}
                      value={itemValue}
                      disabled={disabled}
                      data-groupname={`Group${index}`}
                      ref={createRefForKey(`Group${index}_${itemName}`)}
                    />
                    <Label htmlFor={itemName} noFontWeight={noLabelFrontWeight}>
                      {itemName}
                    </Label>
                  </FormField>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
